import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { NgxMaskDirective } from 'ngx-mask';
import { Observable, debounceTime, distinctUntilChanged, of, startWith, switchMap, tap } from 'rxjs';
import { FeedbackService } from '@app/core/services';
import { ParentAccount } from '@app/modules/fund-management/accounts/models/accounts';
import { AccountSearchComponent } from '@app/shared/components';
import { AccountService, AccountStructureService } from '@app/shared/services';
import * as Transforms from '@app/utils/transforms';

@Component({
    selector: 'account-filter',
    templateUrl: './account-filter.component.html',
    styleUrls: ['./account-filter.component.less'],
    standalone: true,
    imports: [
        MatInputModule,
        MatAutocompleteModule,
        NgxMaskDirective,
        FormsModule,
        ReactiveFormsModule,
        FormlyModule,
        NgIf,
        MatOptionModule,
        NgFor,
        MatIconModule,
        MatFormFieldModule,
        AsyncPipe,
    ],
})
export class AccountFilterComponent extends FieldType<FormlyFieldConfig> implements OnInit {
	unifiedAccountMask: string;
	filter: Observable<any>;
	inputFormControl = new UntypedFormControl('');
	filteredAccounts: any[];

	constructor(
		protected readonly dialog: MatDialog,
		protected readonly accountService: AccountService,
		protected readonly cd: ChangeDetectorRef,
		protected readonly feedbackService: FeedbackService,
		private readonly accountStructureService: AccountStructureService
	) {
		super();
	}

	public ngOnInit(): void {
		this.inputFormControl.setValue(this.formControl.value?.AccountNumber ?? '');
		this.filter = this.inputFormControl.valueChanges.pipe(
			startWith(''),
			distinctUntilChanged(),
			debounceTime(500),
			switchMap((search) => {
				// TODO: investigate why do we put an object into the select or autocomplete since this doesn't make sense and completely wrong
				if (typeof search !== 'object') {
					return this.props.filter(search, this.inputFormControl.value).pipe(
						tap((accounts: any[]) => {
							this.filteredAccounts = accounts;
						})
					);
				}
				return of([]);
			})
		);
		this.accountStructureService.getStructure().subscribe({
			next: (response) => {
				this.unifiedAccountMask = response.accountStructure.UnifiedAccountMask;
				if (this.props.emptyValue) {
					this.inputFormControl.setValue(Transforms.toQuestionMask(this.unifiedAccountMask));
					this.formControl.setValue(Transforms.toQuestionMask(this.unifiedAccountMask));
				}
			},
			error: (error: HttpErrorResponse) => {
				const errorMessage = error.error?.ExceptionMessage || error.error?.Message || '';
				this.feedbackService.error(`There was a problem loading Account Structure: ${errorMessage}`);
			},
		});
	}

	onBlur() {
		if (this.props.blurCleanInput) {
			if (this.inputFormControl.value.length < this.unifiedAccountMask.length) {
				this.inputFormControl.setValue('');
				this.formControl.setValue(null);
			} else {
				const account = this.filteredAccounts.find((el) => el.AccountNumber === this.inputFormControl.value);
				if (!account) {
					this.inputFormControl.setValue('');
					this.formControl.setValue(null);
				}
			}
		} else {
			this.formControl.setValue(this.inputFormControl.value);
		}
	}

	removeAccount() {
		this.formControl.setValue(null);
		this.inputFormControl.setValue('');
	}

	openAccountTable(event: Event): void {
		event.stopPropagation();
		this.dialog
			.open(AccountSearchComponent, {
				closeOnNavigation: false,
				minWidth: '800px',
				disableClose: true,
			})
			.afterClosed()
			.subscribe((resp) => {
				this.onOptionChanged(resp);
			});
	}

	onOptionChanged(selectedOption: MatOption | ParentAccount): void {
		let selectedValue: ParentAccount;
		if (selectedOption instanceof MatOption) {
			selectedValue = selectedOption.value;
		} else {
			selectedValue = selectedOption;
		}
		if (this.props.onOptionChanged) {
			this.props.onOptionChanged(selectedValue);
		}
		this.inputFormControl.setValue(selectedValue.AccountNumber);
		this.formControl.setValue(selectedValue);
		this.formControl.markAsDirty();
	}
}
